import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { UserLogin } from '../models/user-login.model';

declare global {
  interface Window {
    receiveValuesFromNative: (jsonString: string) => void;
    JSInterface: {
      saveInSharedPreferences: (key: string, value: string) => void;
      getValues: (json: string) => void;
      removeValues: (json: string) => void;
      login: (json: string) => void;
      logout: (json: string) => void;
      updateAccessToken: (json: string) => void;
      appInitEnd: (value: string) => void;
      openPDF: (url: string) => void;
      openExternalUrl: (url: string) => void;
      downloadUrl: (json: string) => void;
      uploadFile: () => void;
      startScanner: () => void;
      clearPushNotification: () => void;
      setPortrait: () => void;
      setLandscape: () => void;
    };
    webkit: {
      messageHandlers: {
        saveInSharedPreferences: { postMessage: (payload: unknown) => void };
        getValues: { postMessage: (payload: unknown) => void };
        removeValues: { postMessage: (payload: unknown) => void };
        login: { postMessage: (payload: unknown) => void };
        logout: { postMessage: (payload: unknown) => void };
        updateAccessToken: { postMessage: (payload: unknown) => void };
        appInitEnd: { postMessage: (payload: unknown) => void };
        openPDF: { postMessage: (payload: unknown) => void };
        openExternalUrl: { postMessage: (payload: unknown) => void };
        downloadUrl: { postMessage: (payload: unknown) => void };
        uploadFile: { postMessage: (payload: unknown) => void };
        startScanner: { postMessage: (payload: unknown) => void };
        clearPushNotification: { postMessage: (payload: unknown) => void };
        setPortrait: { postMessage: (payload: unknown) => void };
        setLandscape: { postMessage: (payload: unknown) => void };
      };
    };
  }
}

@Injectable({ providedIn: 'root' })
export class JsBridgeService {

  valuesReady: Promise<Record<string, string> | undefined>;
  private resolveValues!: (value: Record<string, string> | undefined) => void;

  private readonly qrCodeSubject = new Subject<string>();
  readonly qrCodeScanned$ = this.qrCodeSubject.asObservable();

  emitQrCode(value: string): void {
    this.qrCodeSubject.next(value);
  }

  constructor() {
    this.valuesReady = new Promise<Record<string, string> | undefined>(resolve => {
      this.resolveValues = resolve;
    });

    window.receiveValuesFromNative = (jsonString: string) => {
      try {
        const data = JSON.parse(jsonString);
        if (typeof data === 'object' && data !== null && !Array.isArray(data)) {
          Object.keys(data).forEach(key => {
            localStorage.setItem(key, data[key]);
          });
          this.resolveValues(data);
        } else {
          this.resolveValues(undefined);
        }
      } catch {
        this.resolveValues(undefined);
      }
    };
  }

  // --- SharedPreferences ---

  saveSharedPreferences(key: string, value: string): void {
    try {
      window.webkit?.messageHandlers?.saveInSharedPreferences?.postMessage({ key, value });
    } catch { /* iOS not available */ }
    try {
      window.JSInterface?.saveInSharedPreferences(key, value);
    } catch { /* Android not available */ }
  }

  getSharedValues(keys: string[]): void {
    const hasIos = typeof window.webkit?.messageHandlers?.getValues?.postMessage === 'function';
    const hasAndroid = typeof window.JSInterface?.getValues === 'function';

    if (hasIos) {
      try {
        window.webkit.messageHandlers.getValues.postMessage(keys);
      } catch { /* iOS not available */ }
    } else if (hasAndroid) {
      try {
        window.JSInterface.getValues(JSON.stringify(keys));
      } catch { /* Android not available */ }
    } else {
      this.resolveValues(undefined);
    }
  }

  removeSharedValues(keys: string[]): void {
    try {
      window.webkit?.messageHandlers?.removeValues?.postMessage(keys);
    } catch { /* iOS not available */ }
    try {
      window.JSInterface?.removeValues(JSON.stringify(keys));
    } catch { /* Android not available */ }
  }

  // --- Auth ---

  login(data: UserLogin): void {
    const payload = { userId: data.userId, accessToken: data.accessToken };
    try {
      window.webkit?.messageHandlers?.login?.postMessage(payload);
    } catch { /* iOS not available */ }
    try {
      window.JSInterface?.login(JSON.stringify(payload));
    } catch { /* Android not available */ }
  }

  logout(data: UserLogin): void {
    const payload = { userId: data.userId, accessToken: data.accessToken };
    try {
      window.webkit?.messageHandlers?.logout?.postMessage(payload);
    } catch { /* iOS not available */ }
    try {
      window.JSInterface?.logout(JSON.stringify(payload));
    } catch { /* Android not available */ }
  }

  updateAccessToken(data: UserLogin): void {
    const payload = { userId: data.userId, accessToken: data.accessToken };
    try {
      window.webkit?.messageHandlers?.updateAccessToken?.postMessage(payload);
    } catch { /* iOS not available */ }
    try {
      window.JSInterface?.updateAccessToken(JSON.stringify(payload));
    } catch { /* Android not available */ }
  }

  appInitEnd(): void {
    try {
      window.webkit?.messageHandlers?.appInitEnd?.postMessage(true);
    } catch { /* iOS not available */ }
    try {
      window.JSInterface?.appInitEnd('true');
    } catch { /* Android not available */ }
  }

  // --- Utility ---

  openPdf(url: string): void {
    const hasIos = typeof window.webkit?.messageHandlers?.openPDF?.postMessage === 'function';
    const hasAndroid = typeof window.JSInterface?.openPDF === 'function';

    if (hasIos) {
      try {
        window.webkit.messageHandlers.openPDF.postMessage(url);
      } catch { /* iOS not available */ }
    } else if (hasAndroid) {
      try {
        window.JSInterface.openPDF(url);
      } catch { /* Android not available */ }
    } else {
      window.open(url, '_blank');
    }
  }

  openExternalUrl(url: string): void {
    const hasIos = typeof window.webkit?.messageHandlers?.openExternalUrl?.postMessage === 'function';
    const hasAndroid = typeof window.JSInterface?.openExternalUrl === 'function';

    if (hasIos) {
      try {
        window.webkit.messageHandlers.openExternalUrl.postMessage(url);
        return;
      } catch { /* iOS not available */ }
    }

    if (hasAndroid) {
      try {
        window.JSInterface.openExternalUrl(url);
        return;
      } catch { /* Android not available */ }
    }

    window.open(url, '_blank', 'noopener,noreferrer');
  }

  downloadUrl(url: string, fileName: string, mimeType?: string, openExternal: boolean = true): void {
    const payload = { url, fileName, mimeType, openExternal };
    const hasIos = typeof window.webkit?.messageHandlers?.downloadUrl?.postMessage === 'function';
    const hasAndroid = typeof window.JSInterface?.downloadUrl === 'function';

    if (hasIos) {
      try {
        window.webkit.messageHandlers.downloadUrl.postMessage(payload);
        return;
      } catch { /* iOS not available */ }
    }

    if (hasAndroid) {
      try {
        window.JSInterface.downloadUrl(JSON.stringify(payload));
        return;
      } catch { /* Android not available */ }
    }

    window.open(url, '_blank', 'noopener,noreferrer');
  }

  uploadFile(): void {
    try {
      window.webkit?.messageHandlers?.uploadFile?.postMessage(null);
    } catch { /* iOS not available */ }
    try {
      window.JSInterface?.uploadFile();
    } catch { /* Android not available */ }
  }

  startScanner(): void {
    try {
      window.webkit?.messageHandlers?.startScanner?.postMessage(null);
    } catch { /* iOS not available */ }
    try {
      window.JSInterface?.startScanner();
    } catch { /* Android not available */ }
  }

  clearPushNotification(): void {
    try {
      window.webkit?.messageHandlers?.clearPushNotification?.postMessage(null);
    } catch { /* iOS not available */ }
    try {
      window.JSInterface?.clearPushNotification();
    } catch { /* Android not available */ }
  }

  // --- Orientation ---

  setModePortrait(): void {
    try {
      window.webkit?.messageHandlers?.setPortrait?.postMessage(null);
    } catch { /* iOS not available */ }
    try {
      window.JSInterface?.setPortrait();
    } catch { /* Android not available */ }
  }

  setModeLandscape(): void {
    try {
      window.webkit?.messageHandlers?.setLandscape?.postMessage(null);
    } catch { /* iOS not available */ }
    try {
      window.JSInterface?.setLandscape();
    } catch { /* Android not available */ }
  }
}
